import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { ChevronRight, Lock } from "@mui/icons-material";
import {
  arePredictionsLocked,
  getDivisionStandingsAll,
  getUserPredictionsStrict,
  saveStandingsPrediction,
} from "../../services/MatchService";
import { baseURL, currentSeason } from "../../Constants";
import { AuthContext } from "../../context/AuthContext";
import { formatLockCountdown, formatLockDate, usePredictionLock } from "../../hooks/useDeadlineLock";
import { scorePrediction } from "../../utils/predictionScore";
import DivisionPredictor, { LastSeasonStanding } from "./predict/DivisionPredictor";
import PredictionCards from "./predict/PredictionCards";
import {
  FIRST_PREDICTION_SEASON,
  PredictionsByDivision,
  Slots,
  StandingsByDivision,
  compareDivisions,
  formatSavedDate,
  majorLabel,
  majorOf,
  majorsOf,
  predictableDivisions,
  sameSlots,
  sanitizeSlots,
  slotsFromTeams,
  toPredictionMap,
} from "./predict/predictShared";
import styles from "./Predict.module.css";

const SEASONS = Array.from(
  { length: currentSeason - FIRST_PREDICTION_SEASON + 1 },
  (_, i) => currentSeason - i
);

type LoadState = "loading" | "ready" | "error";
type DivisionState = "saved" | "dirty" | "missing";

const dotClass: Record<DivisionState, string> = {
  saved: styles.dotSaved,
  dirty: styles.dotDirty,
  missing: styles.dotMissing,
};

/**
 * /standings/predict — rank the teams of each division. "Alle" shows every division as a card;
 * a division opens the pool -> slots board. ?season= and ?division= keep the view in the URL.
 */
const PredictStandingsPage: React.FC = () => {
  const { t, i18n } = useTranslation(["pages", "common"]);
  const { user, token, authLoaded } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();

  const seasonParam = Number(searchParams.get("season"));
  const season = SEASONS.includes(seasonParam) ? seasonParam : currentSeason;
  const divisionParam = searchParams.get("division") ?? "";

  // Each fetch is stored with the season it belongs to, so a slow response for the season you just
  // left can never be drawn over the one you switched to.
  const [standings, setStandings] = useState<{ season: number; data: StandingsByDivision } | null>(null);
  const [standingsState, setStandingsState] = useState<LoadState>("loading");
  const [standingsReload, setStandingsReload] = useState(0);
  const [lastSeason, setLastSeason] = useState<{ season: number; data: Record<string, LastSeasonStanding> } | null>(null);
  const [predictions, setPredictions] = useState<{ season: number; data: PredictionsByDivision } | null>(null);
  const [predictionsState, setPredictionsState] = useState<LoadState>("loading");
  const [predictionsReload, setPredictionsReload] = useState(0);
  const [seasonLocked, setSeasonLocked] = useState(false);
  // The current season's deadline (the fantasy one), counted down live while predictions are open.
  const lock = usePredictionLock();
  const [drafts, setDrafts] = useState<Record<string, Slots>>({});
  const [justSaved, setJustSaved] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    setDrafts({});
    setJustSaved({});
    setSaveError(null);
  }, [season]);

  useEffect(() => {
    let cancelled = false;
    setStandingsState("loading");
    getDivisionStandingsAll(season)
      .then((data: StandingsByDivision) => {
        if (cancelled) return;
        setStandings({ season, data: data ?? {} });
        setStandingsState("ready");
      })
      .catch(() => {
        if (!cancelled) setStandingsState("error");
      });
    return () => {
      cancelled = true;
    };
  }, [season, standingsReload]);

  useEffect(() => {
    let cancelled = false;
    setSeasonLocked(false);
    arePredictionsLocked(season).then((locked) => {
      if (!cancelled) setSeasonLocked(locked);
    });
    return () => {
      cancelled = true;
    };
  }, [season]);

  const steamId = user?.steamID;
  useEffect(() => {
    if (!authLoaded) return;
    if (!steamId) {
      setPredictions({ season, data: {} });
      setPredictionsState("ready");
      return;
    }
    let cancelled = false;
    setPredictionsState("loading");
    getUserPredictionsStrict(steamId, season)
      .then((list) => {
        if (cancelled) return;
        setPredictions({ season, data: toPredictionMap(list) });
        setPredictionsState("ready");
      })
      .catch(() => {
        if (!cancelled) setPredictionsState("error");
      });
    return () => {
      cancelled = true;
    };
  }, [authLoaded, steamId, season, predictionsReload]);

  const table = standings?.season === season ? standings.data : null;
  const saved = useMemo(
    () => (predictions?.season === season ? predictions.data : {}),
    [predictions, season]
  );
  const divisions = useMemo(() => predictableDivisions(table), [table]);
  // An old link can name a whole major (division=2div): open its first group.
  const division = divisions.includes(divisionParam)
    ? divisionParam
    : divisions.find((d) => majorOf(d) === divisionParam) ?? "";

  // Last season's finishes, for the team panel. One call for every division, only once a division is open.
  useEffect(() => {
    if (!division || lastSeason?.season === season) return;
    let cancelled = false;
    getDivisionStandingsAll(season - 1)
      .then((data: StandingsByDivision) => {
        if (cancelled) return;
        const byTeam: Record<string, LastSeasonStanding> = {};
        for (const key of Object.keys(data ?? {}).sort(compareDivisions)) {
          for (const s of data[key] ?? []) {
            if (s.team && !byTeam[s.team.teamId] && s.gamesPlayed > 0) {
              byTeam[s.team.teamId] = { rank: s.rank, division: key };
            }
          }
        }
        setLastSeason({ season, data: byTeam });
      })
      .catch(() => {
        if (!cancelled) setLastSeason({ season, data: {} });
      });
    return () => {
      cancelled = true;
    };
  }, [division, season, lastSeason]);

  useEffect(() => {
    setSaveError(null);
  }, [division]);

  const signedIn = !!user;
  // lock.locked flips the second the deadline passes, so an open page closes itself without a reload.
  const locked = seasonLocked || season !== currentSeason || lock.locked;
  const lockDate = lock.lockTime ? formatLockDate(lock.lockTime, i18n.language === "en" ? "en-GB" : "da-DK") : "";
  const countdown = formatLockCountdown(lock.msRemaining ?? 0, {
    d: t("common:time.unitDay"),
    h: t("common:time.unitHour"),
    m: t("common:time.unitMinute"),
    s: t("common:time.unitSecond"),
  });
  // Signed out you can still try a ranking; signed in, only once your saved picks have loaded.
  const predictionsReady = !signedIn || predictionsState === "ready";
  const editable = !locked && predictionsReady;

  const baselineFor = (d: string): Slots => slotsFromTeams(saved[d]?.teams, table?.[d] ?? []);
  const slotsFor = (d: string): Slots =>
    drafts[d] ? sanitizeSlots(drafts[d], table?.[d] ?? []) : baselineFor(d);
  const isDirty = (d: string): boolean => !!drafts[d] && !sameSlots(slotsFor(d), baselineFor(d));
  const stateOf = (d: string): DivisionState => (isDirty(d) ? "dirty" : saved[d] ? "saved" : "missing");

  const anyDirty = divisions.some(isDirty);
  useEffect(() => {
    if (!anyDirty) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [anyDirty]);

  const updateParams = (next: { season?: number; division?: string }) => {
    const s = next.season ?? season;
    const d = next.division ?? division;
    const params: Record<string, string> = {};
    if (s !== currentSeason) params.season = String(s);
    if (d) params.division = d;
    setSearchParams(params);
  };

  // Header summary: the same score the leaderboard computes, over the divisions scored so far.
  const savedDivisions = divisions.filter((d) => saved[d]);
  const scores = savedDivisions
    .map((d) => scorePrediction(saved[d].teams, table?.[d]))
    .filter((s): s is NonNullable<typeof s> => s !== null);
  let summary = signedIn
    ? t("predict.summary", { done: savedDivisions.length, total: divisions.length })
    : t("predict.signInToSave");
  if (signedIn && scores.length) {
    const average = scores.reduce((sum, s) => sum + s.score, 0) / scores.length;
    const exact = scores.reduce((sum, s) => sum + s.exact, 0);
    summary += ` · ${t("predict.summaryScore", { score: average.toFixed(2) })} · ${t("predict.summaryExact", { count: exact })}`;
  }

  // The open division's board.
  const standingsOfDivision = division && table ? table[division] ?? [] : [];
  const slots = division ? (locked ? baselineFor(division) : slotsFor(division)) : [];
  const placed = slots.filter(Boolean).length;
  const complete = slots.length > 0 && placed === slots.length;
  const savedPrediction = division ? saved[division] : undefined;
  const dirty = division ? isDirty(division) : false;

  let statusText = "";
  let statusDot = styles.statusDot;
  let statusQuiet = false;
  if (division) {
    const placedText = t("predict.statusPlaced", { placed, total: slots.length });
    if (locked) {
      const score = savedPrediction ? scorePrediction(savedPrediction.teams, standingsOfDivision) : null;
      statusText = score ? t("predict.statusLockedScore", { score: score.score.toFixed(2) }) : t("predict.statusLocked");
      statusDot = `${styles.statusDot} ${styles.statusDotLocked}`;
      statusQuiet = true;
    } else if (!signedIn) {
      statusText = `${placedText} · ${t("predict.statusSignIn")}`;
    } else if (!complete) {
      statusText = savedPrediction && dirty ? `${placedText} · ${t("predict.statusNotSaved")}` : placedText;
    } else if (dirty) {
      statusText = t("predict.statusAllPlaced");
    } else if (savedPrediction) {
      statusText = justSaved[division]
        ? t("predict.statusSavedNow")
        : t("predict.statusSaved", { date: formatSavedDate(savedPrediction.updatedAt, i18n.language) });
      statusDot = `${styles.statusDot} ${styles.statusDotSaved}`;
      statusQuiet = true;
    } else {
      statusText = placedText;
    }
  }

  const save = async () => {
    if (!user || !token || !division || !complete || !dirty) return;
    const teams = slots.map((teamId, i) => ({ teamId: teamId as string, predictedRank: i + 1 }));
    setSaving(true);
    setSaveError(null);
    try {
      await saveStandingsPrediction(token, { steamId: user.steamID, season, division, teams }, !!savedPrediction);
      const now = new Date();
      setPredictions((prev) => {
        const data = prev?.season === season ? prev.data : {};
        return {
          season,
          data: {
            ...data,
            [division]: {
              predictionId: data[division]?.predictionId ?? 0,
              steamId: user.steamID,
              season,
              division,
              locked: false,
              createdAt: data[division]?.createdAt ?? now,
              updatedAt: now,
              teams,
            },
          },
        };
      });
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[division];
        return next;
      });
      setJustSaved((prev) => ({ ...prev, [division]: true }));
    } catch (err) {
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;
      const code = axios.isAxiosError(err) ? err.response?.data?.error : undefined;
      if (status === 401) setSaveError(t("predict.errorSession"));
      else if (status === 403) setSaveError(t("predict.errorWrongUser"));
      else if (status === 409 && code === "teamsChanged") {
        // Reload the teams; the draft keeps every pick that still fits and leaves the rest empty.
        setSaveError(t("predict.errorTeamsChanged"));
        setStandingsReload((n) => n + 1);
      } else if (status === 409) {
        setSeasonLocked(true);
        setSaveError(t("predict.errorLocked"));
      } else if (status === 503) setSaveError(t("predict.errorUnavailable"));
      else setSaveError(t("predict.errorGeneric"));
    } finally {
      setSaving(false);
    }
  };

  const steamLogin = () => {
    window.location.href = `${baseURL}/Auth/steam`;
  };

  const majors = majorsOf(divisions);
  const openMajor = division ? majorOf(division) : "";
  const groups = majors.find((m) => m.major === openMajor)?.divisions ?? [];

  const aggregate = (list: string[]): DivisionState => {
    const states = list.map(stateOf);
    return states.includes("dirty") ? "dirty" : states.includes("missing") ? "missing" : "saved";
  };

  const pillClass = (active: boolean) => `${styles.pill} ${active ? styles.pillActive : ""}`;

  return (
    <div className={styles.page}>
      <div className={styles.head}>
        <div>
          <h1 className={styles.title}>{t("predict.title")}</h1>
          <div className={styles.statusLine}>
            {locked ? (
              <span className={styles.statusItem}>
                <Lock sx={{ fontSize: 14 }} />
                {season !== currentSeason ? t("predict.pastSeason") : t("predict.lockedSeason")}
              </span>
            ) : (
              <span className={`${styles.statusItem} ${lock.urgent ? styles.statusUrgent : ""}`}>
                <span className={styles.openDot} />
                {lock.msRemaining
                  ? t("predict.openUntil", { time: countdown, date: lockDate })
                  : t("predict.open")}
              </span>
            )}
            {table && authLoaded && (
              <>
                <span className={styles.statusSep}>·</span>
                <span>{summary}</span>
              </>
            )}
          </div>
        </div>
        <div className={styles.headRight}>
          <div role="group" aria-label={t("predict.seasonAria")} className={styles.pillGroup}>
            {SEASONS.map((s) => (
              <button
                key={s}
                type="button"
                className={pillClass(s === season)}
                aria-pressed={s === season}
                onClick={() => s !== season && updateParams({ season: s })}
              >
                {s === season ? t("common:seasonNum", { number: s }) : s}
              </button>
            ))}
          </div>
          <Link
            className={styles.linkButton}
            to={season === currentSeason ? "/standings/leaderboard" : `/standings/leaderboard?season=${season}`}
          >
            {t("predict.leaderboard")}
            <ChevronRight sx={{ fontSize: 18 }} />
          </Link>
        </div>
      </div>

      <div className={styles.controls}>
        {divisions.length > 0 && (
          <div role="group" aria-label={t("predict.divisionAria")} className={styles.pillGroup}>
            <button type="button" className={pillClass(!division)} aria-pressed={!division} onClick={() => updateParams({ division: "" })}>
              {t("predict.all")}
            </button>
            {majors.map((m) => {
              const active = m.major === openMajor;
              return (
                <button
                  key={m.major}
                  type="button"
                  className={pillClass(active)}
                  aria-pressed={active}
                  onClick={() => !active && updateParams({ division: m.divisions[0] })}
                >
                  {majorLabel(m.major)}
                  <span className={`${styles.pillDot} ${dotClass[aggregate(m.divisions)]}`} />
                </button>
              );
            })}
          </div>
        )}
        {groups.length > 1 && (
          <div role="group" aria-label={t("predict.groupAria")} className={styles.pillGroup}>
            {groups.map((d) => (
              <button
                key={d}
                type="button"
                className={pillClass(d === division)}
                aria-pressed={d === division}
                onClick={() => d !== division && updateParams({ division: d })}
              >
                {d.slice(-1)}
                <span className={`${styles.pillDot} ${dotClass[stateOf(d)]}`} />
              </button>
            ))}
          </div>
        )}
        <span className={styles.spacer} />
        {division && table && (
          <div className={styles.saveArea}>
            <span className={`${styles.status} ${statusQuiet ? styles.statusQuiet : ""}`}>
              <span className={statusDot} />
              {statusText}
            </span>
            {!locked && signedIn && (
              <button
                type="button"
                className={styles.primaryButton}
                onClick={save}
                disabled={!complete || !dirty || saving || !predictionsReady}
              >
                {saving ? t("predict.saving") : t("predict.savePrediction")}
              </button>
            )}
            {!locked && authLoaded && !signedIn && (
              <button type="button" className={styles.primaryButton} onClick={steamLogin}>
                {t("predict.signIn")}
              </button>
            )}
          </div>
        )}
      </div>

      {saveError && <div className={`${styles.note} ${styles.noteError}`}>{saveError}</div>}
      {signedIn && predictionsState === "error" && (
        <div className={`${styles.note} ${styles.noteError}`}>
          {t("predict.predictionsLoadError")}{" "}
          <button type="button" className={styles.ghostButton} onClick={() => setPredictionsReload((n) => n + 1)}>
            {t("predict.retry")}
          </button>
        </div>
      )}

      {standingsState === "error" && !table ? (
        <div className={`${styles.note} ${styles.noteError}`}>
          {t("predict.loadError")}{" "}
          <button type="button" className={styles.ghostButton} onClick={() => setStandingsReload((n) => n + 1)}>
            {t("predict.retry")}
          </button>
        </div>
      ) : !table ? (
        <div className={styles.loadingText}>{t("predict.loading")}</div>
      ) : division ? (
        <DivisionPredictor
          key={`${season}-${division}`}
          season={season}
          division={division}
          standings={standingsOfDivision}
          lastSeason={lastSeason?.season === season ? lastSeason.data : {}}
          slots={slots}
          onSlotsChange={(next) => setDrafts((prev) => ({ ...prev, [division]: next }))}
          editable={editable}
          locked={locked}
          canUndo={dirty && !!savedPrediction}
          onUndo={() =>
            setDrafts((prev) => {
              const next = { ...prev };
              delete next[division];
              return next;
            })
          }
        />
      ) : authLoaded && !signedIn ? (
        <div className={`${styles.panel} ${styles.signInPanel}`}>
          <span className={styles.cardEmptyTitle}>{t("predict.loginToView")}</span>
          <span className={styles.cardEmptyText}>{t("predict.signInHint")}</span>
          <button type="button" className={styles.primaryButton} onClick={steamLogin}>
            {t("predict.signIn")}
          </button>
        </div>
      ) : predictionsState === "loading" || !authLoaded ? (
        <div className={styles.loadingText}>{t("predict.loading")}</div>
      ) : (
        <PredictionCards
          season={season}
          divisions={divisions}
          standings={table}
          predictions={saved}
          justSaved={justSaved}
          owner="self"
          locked={locked || predictionsState === "error"}
          onOpen={(d) => updateParams({ division: d })}
        />
      )}
    </div>
  );
};

export default PredictStandingsPage;
